import { parseISO } from "date-fns";
import { Flex, Text, HelpIndicator } from "@jsq/ds/core";
import { Icon, IconType } from "@jsq/ds/icons";
import { gridCellStyles } from "~/pages/Compliance/Common/GridCellBackgrounds/styles_vanilla.css";
import { BaseStatusTooltip } from "~/pages/Compliance/ProfilesTable/BaseStatusTooltip";
import { DOCUMENT_STATUS_CONFIG } from "~/pages/Compliance/ProfilesTable/DocumentRefreshCellRenderer/config";
import {
  baseContainer,
  contentWrapper,
  darkText,
  statusLabelAnchor,
} from "~/pages/Compliance/ProfilesTable/DocumentRefreshCellRenderer/styles_vanilla.css";
import { formatShortFormDate } from "~/utils/datetime";
import { getExpiryStatus } from "./utils";

interface ExpirationDateCellRendererProps {
  value: unknown;
}

export const ExpirationDateCellRenderer = ({ value }: ExpirationDateCellRendererProps) => {
  const dateStr = value as string | null | undefined;
  const statusEnum = getExpiryStatus(dateStr);
  const formattedDate = dateStr ? formatShortFormDate(dateStr) : "--";

  if (statusEnum) {
    const config = DOCUMENT_STATUS_CONFIG[statusEnum];
    if (!config) return null;

    const expiry = parseISO(dateStr!);
    const tooltipContent = BaseStatusTooltip({
      statusType: statusEnum,
      tooltipData: { nextDocumentExpiry: expiry },
    });

    const iconAndText = (
      <span className={statusLabelAnchor}>
        {config.icon && (
          <Icon
            type={config.icon.type as IconType}
            variant={config.icon.variant}
            className={config.icon.className}
            data-testid={`expiration-date-${statusEnum}-icon`}
          />
        )}
        <Text className={config.text.className} data-testid="expiration-date-label">
          {formattedDate}
        </Text>
      </span>
    );

    return (
      <Flex className={config.containerClass} alignItems="center">
        <div className={contentWrapper}>
          {tooltipContent ? (
            <HelpIndicator
              id={`expiration-date-${statusEnum}-tooltip`}
              placement="top"
              content={tooltipContent}
              fullWidth
            >
              {iconAndText}
            </HelpIndicator>
          ) : (
            iconAndText
          )}
        </div>
      </Flex>
    );
  }

  return (
    <Flex className={`${baseContainer} ${gridCellStyles.default}`} alignItems="center">
      <div className={contentWrapper}>
        <Text className={darkText} data-testid="expiration-date-label">
          {formattedDate}
        </Text>
      </div>
    </Flex>
  );
};
